import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { IoButtonComponent } from './shared/components/io-button/io-button.component';
import { IoSidenavComponent } from './shared/components/io-sidenav/io-sidenav.component';
import { IoTopbarComponent } from './shared/components/io-topbar/io-topbar.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, filter, forkJoin, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { IoInputTextComponent } from './shared/components/io-input-text/io-input-text.component';
import { IoMenuComponent } from './shared/components/io-menu/io-menu.component';
import { AuthService } from './core/services/api/auth.service';
import { AppService } from './core/services/app.service';
import { User } from './core/models/user.model';
import { ToastModule } from 'primeng/toast';
import { TranslationService } from './core/services/translation.service';
import { IoLanguageSwitcherComponent } from './shared/components/io-language-switcher/io-language-switcher.component';
import { ProjectService } from './core/services/api/project.service';
import { WorkitemDefinitionService } from './core/services/api/workitem-definition.service';
import { ApiResponse } from './core/models/api-response.model';
import { StrapiQueryParams } from './core/models/strapi-query.model';
import { ProjectItem } from './core/models/project-item.model';
import { WorkitemDefinitionItem } from './core/models/workitem-definition-item.model';
import { NotificationBellComponent } from './shared/components/notification-bell/notification-bell.component';

@Component({
  selector: 'app-root',
  standalone: true,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    RouterOutlet,
    IoTopbarComponent,
    IoSidenavComponent,
    IoButtonComponent,
    IoInputTextComponent,
    IoMenuComponent,
    IoLanguageSwitcherComponent,
    NotificationBellComponent,
    TranslatePipe,
    CommonModule,
    ToastModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  readonly logoSrc = 'assets/images/logo-ntsh.png';
  @ViewChild(IoMenuComponent) private userMenu?: IoMenuComponent;
  sidenavVisible = true;
  sidenavStatus: 'hidden' | 'collapsed' | 'opened' = 'opened';
  readonly hideShell = signal(true);
  readonly titleKey = signal('header.title');
  readonly subtitleKey = signal('header.subtitle');
  readonly activeSidenavIndex = signal<number | null>(null);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private authService = inject(AuthService);
  private appService = inject(AppService);
  private translate = inject(TranslateService);
  readonly i18n = inject(TranslationService);
  private projectService = inject(ProjectService);
  private workitemDefinitionService = inject(WorkitemDefinitionService);

  /** Total projects (Strapi pagination); `null` until loaded or on error. */
  readonly sidenavProjectTotal = signal<number | null>(null);
  /** Total work item definitions for `/work-items`; `null` until loaded or on error. */
  readonly sidenavWorkItemDefinitionTotal = signal<number | null>(null);
  menuToggle = output<void>();
  showMenuToggle = input<boolean>(false);
  collapsed = input<boolean>(false);
  activeRoute = input<string>('');

  toggleCollapse = output<void>();

  readonly currentUser = signal<User | null>(null);
  readonly userName = computed(() => this.currentUser()?.username ?? '');

  readonly userRole = computed(() => this.currentUser()?.role?.name ?? '');

  readonly iconKey = signal('');
  readonly searchValue = signal('');

  constructor() {
    this.updateShellVisibility(this.getCurrentUrl());
  }

  readonly userInitials = computed(() => {
    const [firstName, lastName] = this.userName().split(' ');
    const firstInitial = firstName?.charAt(0) ?? '';
    const lastInitial = lastName?.charAt(0) ?? '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  });
  readonly menuActions = computed<MenuItem[]>(() => {
    this.i18n.currentLanguage();
    return [
      {
        label: this.translate.instant('header.account.myAccount'),
        disabled: true,
        styleClass: 'account-menu__title-item',
      },
      {
        separator: true,
      },
      {
        label: this.translate.instant('header.account.profileSettings'),
        icon: 'pi pi-user',
        command: () => this.router.navigate(['/profile']),
      },
      {
        label: this.translate.instant('header.account.preferences'),
        icon: 'pi pi-cog',
        command: () => this.router.navigate(['/settings']),
      },
      {
        label: this.translate.instant('header.account.teamManagement'),
        icon: 'pi pi-users',
        command: () => this.router.navigate(['/employees']),
      },
      {
        separator: true,
      },
      {
        label: this.translate.instant('header.account.signOut'),
        icon: 'pi pi-sign-out',
        styleClass: 'account-menu__danger-item',
        command: () => this.onSignOutClicked(),
      },
    ];
  });

  readonly menuItems = computed<MenuItem[]>(() => {
    this.i18n.currentLanguage();
    this.sidenavProjectTotal();
    this.sidenavWorkItemDefinitionTotal();
    const projectBadge = this.sidenavProjectTotal() != null ? String(this.sidenavProjectTotal()!) : undefined;
    const workItemsBadge =
      this.sidenavWorkItemDefinitionTotal() != null ? String(this.sidenavWorkItemDefinitionTotal()!) : undefined;
    return [
      {
        label: this.translate.instant('sidebar.main'),
        separator: true,
      },

      {
        label: this.translate.instant('sidebar.projects'),
        icon: '/assets/projects.svg',
        routerLink: '/projects',
        command: () => this.router.navigateByUrl('/projects'),
        badge: projectBadge,
      },
      {
        label: this.translate.instant('sidebar.workflows'),
        icon: '/assets/workflow.svg',
        routerLink: '/workflows',
        command: () => this.router.navigateByUrl('/workflows'),
      },
      {
        label: this.translate.instant('sidebar.workItems'),
        icon: '/assets/work-item.svg',
        routerLink: '/work-items',
        command: () => this.router.navigateByUrl('/work-items'),
        badge: workItemsBadge,
      },
      {
        label: this.translate.instant('sidebar.resource'),
        separator: true,
      },
      {
        label: this.translate.instant('sidebar.employees'),
        icon: '/assets/team.svg',
        routerLink: '/employees',
        command: () => this.router.navigateByUrl('/employees'),
      },
      {
        label: this.translate.instant('sidebar.clients'),
        icon: 'pi pi-building',
        routerLink: '/clients',
        command: () => this.router.navigateByUrl('/clients'),
      },
      {
        label: this.translate.instant('sidebar.configAssets'),
        icon: '/assets/setting.svg',
        routerLink: '/config-assets',
        command: () => this.router.navigateByUrl('/config-assets'),
      },
    ];
  });

  onToggleCollapse(): void {
    this.toggleCollapse.emit();
  }

  isActive(route: string): boolean {
    const activeRoute = this.activeRoute();
    if (!activeRoute) return false;
    return activeRoute === route || activeRoute.startsWith(route + '/');
  }

  ngOnInit(): void {
    this.authService.user$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(user => {
          this.currentUser.set(user);
          if (!user) {
            this.sidenavProjectTotal.set(null);
            this.sidenavWorkItemDefinitionTotal.set(null);
          } else {
            this.refreshSidenavCounters();
          }
        }),
      )
      .subscribe();

    this.workitemDefinitionService.definitionCountRefreshRequested$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshSidenavCounters());
    this.projectService.projectCountRefreshRequested$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshSidenavCounters());

    this.updateShellVisibility(this.getCurrentUrl());
    this.updateActiveSidenavIndex(this.router.url);
    this.updateHeaderFromUrl(this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(event => {
        this.updateShellVisibility(event.urlAfterRedirects);
        this.updateActiveSidenavIndex(event.urlAfterRedirects);
        this.updateHeaderFromUrl(event.urlAfterRedirects);
      });
  }

  private updateShellVisibility(url: string): void {
    const routePath = url.split('?')[0].split('/').filter(Boolean)[0] ?? '';
    this.hideShell.set(routePath === 'login' || routePath === 'register' || routePath === 'forgot-password');
  }

  private getCurrentUrl(): string {
    if (typeof window !== 'undefined') {
      return `${window.location.pathname}${window.location.search}`;
    }
    return this.router.url;
  }

  private updateActiveSidenavIndex(url: string): void {
    const routePath = '/' + (url.split('?')[0].split('/').filter(Boolean)[0] ?? '');
    const index = this.menuItems().findIndex(item => {
      const itemRoute = Array.isArray(item.routerLink) ? item.routerLink.join('/') : item.routerLink;
      if (typeof itemRoute === 'string' && itemRoute) {
        return routePath === itemRoute || routePath.startsWith(itemRoute + '/');
      }

      return false;
    });

    this.activeSidenavIndex.set(index >= 0 ? index : null);
  }

  private updateHeaderFromUrl(url: string): void {
    const routePath = url.split('?')[0].split('/').filter(Boolean)[0] ?? '';

    switch (routePath) {
      case 'employees':
        this.titleKey.set('header.employeesTitle');
        this.subtitleKey.set('header.employeesSubtitle');
        break;

      case 'clients':
        this.titleKey.set('header.clientsTitle');
        this.subtitleKey.set('header.clientsSubtitle');
        break;

      case 'work-items':
        this.titleKey.set('header.workItemsDefinitionTitle');
        this.subtitleKey.set('header.workItemsDefinitionSubtitle');
        break;

      case 'workflows':
        this.titleKey.set('header.workflowsTitle');
        this.subtitleKey.set('header.workflowsSubtitle');
        break;

      case 'projects':
        this.titleKey.set('header.title');
        this.subtitleKey.set('header.subtitle');
        break;

      case 'config-assets':
        this.titleKey.set('header.configAssetsTitle');
        this.subtitleKey.set('header.configAssetsSubtitle');
        break;

      default:
        this.titleKey.set('header.title');
        this.subtitleKey.set('header.subtitle');
        break;
    }
  }
  toggleSidenav(): void {
    this.sidenavStatus = this.sidenavStatus === 'collapsed' ? 'opened' : 'collapsed';
  }

  onSearchChange(searchValue: string): void {
    this.searchValue.set(searchValue);
  }

  onFilterChange(_filterValue: string): void {}

  onMenuClick(): void {
    this.menuToggle.emit();
  }

  onUserMenuToggle(event: Event): void {
    this.userMenu?.toggle(event);
  }

  onSignOutClicked(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
    this.appService.snackBarSuccess(this.translate.instant('header.signedOutSuccess'));
  }

  private refreshSidenavCounters(): void {
    if (!this.currentUser()) {
      this.sidenavProjectTotal.set(null);
      this.sidenavWorkItemDefinitionTotal.set(null);
      return;
    }

    const countParams: StrapiQueryParams = {
      pagination: { page: 1, pageSize: 1 },
    };

    forkJoin({
      projects: this.projectService
        .getAllWithMeta(countParams)
        .pipe(catchError(() => of(null as ApiResponse<ProjectItem[]> | null))),
      workItemDefinitions: this.workitemDefinitionService
        .getAllWithMeta(countParams)
        .pipe(catchError(() => of(null as ApiResponse<WorkitemDefinitionItem[]> | null))),
    }).subscribe(({ projects, workItemDefinitions }) => {
      const pTotal = projects?.meta?.pagination?.total;
      this.sidenavProjectTotal.set(typeof pTotal === 'number' ? pTotal : null);
      const wTotal = workItemDefinitions?.meta?.pagination?.total;
      this.sidenavWorkItemDefinitionTotal.set(typeof wTotal === 'number' ? wTotal : null);
    });
  }
}
